import { createContext, useContext } from "react";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { SavedRun } from "../../../packages/contracts/src/workspace";

export const WorkspaceScope = createContext("local");
export const useWorkspaceScope = () => useContext(WorkspaceScope);

export const methodQuery = (scope: string, id?: string) => queryOptions({
  queryKey: ["workflow", scope, id],
  queryFn: () => api.get(id!),
  enabled: !!id,
});
export const runQueryOptions = (scope: string, id?: string) => queryOptions({
  queryKey: ["run", scope, id],
  queryFn: () => api.run(id!),
  enabled: !!id,
  // A failed, waiting or unconfirmed run can still change: it can continue, or a later observation can confirm it.
  refetchInterval: query => ["running", "disconnected"].includes(query.state.data?.status ?? "") ? 3000 : ["failed", "waiting_for_you", "unconfirmed"].includes(query.state.data?.status ?? "") ? 5000 : false,
});
// A run list refreshes often only while one of its runs can still change.
// A focused window also refreshes it.
export const runListRefresh = (query: { state: { data?: SavedRun[] } }) =>
  query.state.data?.some(run => ["running", "disconnected"].includes(run.status)) ? 5000 : 30000;
export const methodRunsQuery = (scope: string, id: string) => queryOptions({
  queryKey: ["runs", "method", scope, id], queryFn: () => api.runs(id), refetchInterval: runListRefresh,
});
export const versionsQuery = (scope: string, id: string) => queryOptions({
  queryKey: ["versions", scope, id], queryFn: () => api.versions(id),
});
export const versionQuery = (scope: string, id: string, version?: string) => queryOptions({
  queryKey: ["version", scope, id, version], queryFn: () => api.version(id, version!), enabled: !!version,
});

export function useWorkspacePrefetch() {
  const client = useQueryClient();
  const scope = useWorkspaceScope();
  return {
    method: (id: string) => {
      void client.prefetchQuery(methodQuery(scope, id));
      void client.prefetchQuery(methodRunsQuery(scope, id));
      void client.prefetchQuery(versionsQuery(scope, id));
    },
    run: (id: string, methodId: string) => {
      void client.prefetchQuery(runQueryOptions(scope, id));
      void client.prefetchQuery(methodQuery(scope, methodId));
    },
  };
}
